import { useState, useEffect, useRef, useMemo } from "react";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

const SearchableDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Search...",
  isLoading = false,
  onSearch,
  disabled = false,
  fullWidth = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const dropdownRef = useRef(null);

  // Use useMemo to prevent unnecessary re-renders when options array is recreated
  const memoizedOptions = useMemo(() => {
    // Safely handle null/undefined options
    if (!options || !Array.isArray(options)) {
      return [];
    }
    return options;
  }, [options?.length, JSON.stringify(options || [])]);

  useEffect(() => {
    // Only update if options actually changed
    if (
      JSON.stringify(memoizedOptions) !== JSON.stringify(filteredOptions) ||
      searchInput === ""
    ) {
      setFilteredOptions(memoizedOptions);
    }
  }, [memoizedOptions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setSearchInput(inputValue);

    if (onSearch) {
      onSearch(inputValue);
    } else {
      const filtered = memoizedOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  };

  const handleSelect = (selectedValue) => {
    if (onChange) {
      onChange(selectedValue);
    }
    setIsOpen(false);
    setSearchInput("");
    setFilteredOptions(memoizedOptions);
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (onChange) {
      onChange(null);
    }
    setSearchInput("");
    setFilteredOptions(memoizedOptions);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchInput("");
        setFilteredOptions(memoizedOptions);
      }
    }
  };

  return (
    <div
      className={`relative ${fullWidth ? "w-full" : "w-full md:w-[200px]"}`}
      ref={dropdownRef}
    >
      <div className="relative">
        <div
          className={`flex items-center justify-between w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ${
            disabled ? "ring-gray-200 bg-gray-50" : "ring-gray-300"
          } focus:ring-2 focus:ring-primary sm:text-sm sm:leading-6 cursor-pointer`}
          onClick={toggleDropdown}
          style={{
            cursor: disabled === true ? "not-allowed" : "pointer",
          }}
        >
          {value !== undefined && value !== null && value !== "" ? (
            <span className="truncate">
              {memoizedOptions.find((opt) => opt.value === value)?.label || value}
            </span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}

          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {value !== undefined && value !== null && value !== "" && !disabled && (
              <button
                type="button"
                className="p-1 hover:bg-gray-100 rounded z-10"
                onClick={clearSelection}
                tabIndex={-1}
              >
                <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
            <ChevronDownIcon
              className={`h-4 w-4 text-gray-400 transition-transform ${
                isOpen ? "transform rotate-180" : ""
              }`}
              aria-hidden="true"
            />
          </div>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="p-2 border-b">
            <div className="relative">
              <input
                type="text"
                className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary sm:text-sm sm:leading-6"
                placeholder="Search..."
                value={searchInput}
                onChange={handleInputChange}
                autoFocus
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                {isLoading ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="animate-spin h-5 w-5 text-gray-400"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke={"currentColor"}
                    fill={"none"}
                  >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                ) : (
                  <MagnifyingGlassIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="max-h-60 overflow-auto py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                return (
                  <div
                    key={option.value}
                    className={`px-4 py-2 text-sm cursor-pointer hover:bg-blue-50 ${
                      value === option.value
                        ? "bg-blue-100 text-primary font-medium"
                        : "text-gray-700"
                    }`}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-2 text-sm text-gray-500">
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
